import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaEye, FaEyeSlash, FaSyncAlt, FaCopy, FaSave, FaMobileAlt, FaLink } from 'react-icons/fa';
import Select from 'react-select';
import { rootUrl } from '../../../config/config';
import { useAuth } from '../AuthContext';
import Alerty from '../../common/Alerty';
import MpesaRegisterModal from './MpesaRegisterModal';

const MASK = '••••••••';
const ENV_OPTIONS = [
  { value: 'sandbox', label: 'Sandbox' },
  { value: 'production', label: 'Production' },
];
const NOTIFY_OPTIONS = [
  { value: 'false', label: 'Disabled' },
  { value: 'true', label: 'Enabled' },
];

const SensitiveField = ({ fieldKey, label, form, revealed, handleChange, handleReveal }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type={revealed[fieldKey] ? 'text' : 'password'}
        value={form[fieldKey]}
        onChange={e => handleChange(fieldKey, e.target.value)}
        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
        placeholder={revealed[fieldKey] ? '' : MASK}
      />
      <button
        type="button"
        onClick={() => handleReveal(fieldKey)}
        className="text-gray-500 hover:text-orange-500 transition-colors"
        title={revealed[fieldKey] ? 'Hide' : 'Show'}
      >
        {revealed[fieldKey] ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>
  </div>
);

const emptyForm = {
  mpesa_consumer_key: '',
  mpesa_consumer_secret: '',
  mpesa_shortcode: '',
  mpesa_passkey: '',
  mpesa_environment: 'sandbox',
  mpesa_callback_secret: '',
  mpesa_initiator_name: '',
  mpesa_initiator_security_credential: '',
  mpesa_c2b_discord_notify: 'false',
};

const MpesaSettings = () => {
  const { token } = useAuth();
  const authToken = token || localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${authToken}` };

  const [form, setForm] = useState(emptyForm);
  const [revealed, setRevealed] = useState({});
  const [confirmUrl, setConfirmUrl] = useState('');
  const [validateUrl, setValidateUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertColor, setAlertColor] = useState('green');
  const [alertMsg, setAlertMsg] = useState('');
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [savedEnvironment, setSavedEnvironment] = useState('sandbox');
  const [savedConfirmUrl, setSavedConfirmUrl] = useState('');
  const [savedValidateUrl, setSavedValidateUrl] = useState('');


  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${rootUrl}/api/mpesa-settings`, { headers });
      const d = res.data.data;
      setForm({
        mpesa_consumer_key: d.mpesa_consumer_key || '',
        mpesa_consumer_secret: d.mpesa_consumer_secret || '',
        mpesa_shortcode: d.mpesa_shortcode || '',
        mpesa_passkey: d.mpesa_passkey || '',
        mpesa_environment: d.mpesa_environment || 'sandbox',
        mpesa_callback_secret: d.mpesa_callback_secret || '',
        mpesa_initiator_name: d.mpesa_initiator_name || '',
        mpesa_initiator_security_credential: d.mpesa_initiator_security_credential || '',
        mpesa_c2b_discord_notify: d.mpesa_c2b_discord_notify === 'true' ? 'true' : 'false',
      });
      setConfirmUrl(d.confirm_url || '');
      setValidateUrl(d.validate_url || '');
      setSavedConfirmUrl(d.confirm_url || '');
      setSavedValidateUrl(d.validate_url || '');
      setSavedEnvironment(d.mpesa_environment || 'sandbox');
      setRevealed({});
    } catch {
      setAlertColor('red');
      setAlertMsg('Failed to load M-Pesa settings');
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchSettings(); }, []);

  const handleReveal = async (key) => {
    if (revealed[key]) {
      setRevealed(prev => ({ ...prev, [key]: false }));
      try {
        const res = await axios.get(`${rootUrl}/api/mpesa-settings`, { headers });
        const d = res.data.data;
        setForm(prev => ({ ...prev, [key]: d[key] || '' }));
      } catch {
        setAlertColor('red');
        setAlertMsg('Failed to hide setting');
        setAlertOpen(true);
      }
      return;
    }
    try {
      const res = await axios.get(`${rootUrl}/api/mpesa-settings/reveal`, { headers });
      const d = res.data.data;
      setForm(prev => ({ ...prev, [key]: d[key] || '' }));
      setRevealed(prev => ({ ...prev, [key]: true }));
    } catch {
      setAlertColor('red');
      setAlertMsg('Failed to reveal setting');
      setAlertOpen(true);
    }
  };

  const handleRegenerate = () => {
    const arr = new Uint8Array(16);
    crypto.getRandomValues(arr);
    const secret = Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
    setForm(prev => ({ ...prev, mpesa_callback_secret: secret }));
    setRevealed(prev => ({ ...prev, mpesa_callback_secret: true }));
  };

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${rootUrl}/api/mpesa-settings`, form, { headers });
      setAlertColor('green');
      setAlertMsg('M-Pesa settings saved successfully');
      setAlertOpen(true);
      fetchSettings();
    } catch {
      setAlertColor('red');
      setAlertMsg('Failed to save M-Pesa settings');
      setAlertOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  if (loading) {
    return <div className="text-gray-500 text-sm py-8 text-center">Loading M-Pesa settings…</div>;
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Alerty
        open={alertOpen}
        color={alertColor}
        message={alertMsg}
        onClose={() => setAlertOpen(false)}
      />

      {/* Card 1: API Credentials */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <FaMobileAlt className="text-orange-500" /> API Credentials
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SensitiveField fieldKey="mpesa_consumer_key" label="Consumer Key" form={form} revealed={revealed} handleChange={handleChange} handleReveal={handleReveal} />
          <SensitiveField fieldKey="mpesa_consumer_secret" label="Consumer Secret" form={form} revealed={revealed} handleChange={handleChange} handleReveal={handleReveal} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Shortcode (Paybill)</label>
            <input
              type="text"
              value={form.mpesa_shortcode}
              onChange={e => handleChange('mpesa_shortcode', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="e.g. 600123"
            />
          </div>
          <SensitiveField fieldKey="mpesa_passkey" label="Passkey" form={form} revealed={revealed} handleChange={handleChange} handleReveal={handleReveal} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Initiator Name</label>
            <input
              type="text"
              value={form.mpesa_initiator_name}
              onChange={e => handleChange('mpesa_initiator_name', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
              placeholder="e.g. apiop_pawafleet"
            />
          </div>
          <SensitiveField fieldKey="mpesa_initiator_security_credential" label="Initiator Security Credential" form={form} revealed={revealed} handleChange={handleChange} handleReveal={handleReveal} />
        </div>
      </div>

      {/* Card 2: Integration Settings */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Integration Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
            <Select
              options={ENV_OPTIONS}
              value={ENV_OPTIONS.find(o => o.value === form.mpesa_environment) || ENV_OPTIONS[0]}
              onChange={opt => handleChange('mpesa_environment', opt.value)}
              classNamePrefix="react-select"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Callback Secret Token</label>
            <div className="flex items-center gap-2">
              <input
                type={revealed.mpesa_callback_secret ? 'text' : 'password'}
                value={form.mpesa_callback_secret}
                onChange={e => handleChange('mpesa_callback_secret', e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                placeholder="Enter or regenerate"
              />
              <button
                type="button"
                onClick={() => handleReveal('mpesa_callback_secret')}
                className="text-gray-500 hover:text-orange-500 transition-colors"
                title={revealed.mpesa_callback_secret ? 'Hide' : 'Show'}
              >
                {revealed.mpesa_callback_secret ? <FaEyeSlash /> : <FaEye />}
              </button>
              <button
                type="button"
                onClick={handleRegenerate}
                className="text-gray-500 hover:text-orange-500 transition-colors"
                title="Regenerate"
              >
                <FaSyncAlt />
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">C2B Discord Alerts</label>
            <Select
              options={NOTIFY_OPTIONS}
              value={NOTIFY_OPTIONS.find(o => o.value === form.mpesa_c2b_discord_notify) || NOTIFY_OPTIONS[0]}
              onChange={opt => handleChange('mpesa_c2b_discord_notify', opt.value)}
              classNamePrefix="react-select"
            />
            <p className="text-xs text-gray-500 mt-1">Post a Discord alert when a C2B payment is posted to a car.</p>
          </div>
        </div>
      </div>

      {/* Card 3: Callback URLs */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
        <h3 className="text-base font-semibold text-amber-800 mb-1">Daraja Callback URLs</h3>
        <p className="text-xs text-amber-700 mb-4">Register these in the Safaricom Daraja portal. URLs update when you save a new Callback Secret Token.</p>
        {[
          { label: 'Confirmation URL', url: confirmUrl },
          { label: 'Validation URL', url: validateUrl },
        ].map(({ label, url }) => (
          <div key={label} className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="text-xs font-medium text-amber-800">{label}</div>
              <div className="text-xs text-amber-700 break-all">{url || '— save a Callback Secret Token first —'}</div>
            </div>
            {url && (
              <button
                type="button"
                onClick={() => copyToClipboard(url)}
                className="shrink-0 flex items-center gap-1 text-xs bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 transition-colors"
              >
                <FaCopy /> Copy
              </button>
            )}
          </div>
        ))}
        <div className="mt-4 pt-4 border-t border-amber-200">
          <button
            type="button"
            onClick={() => setRegisterModalOpen(true)}
            disabled={!confirmUrl || !validateUrl}
            className="flex items-center gap-2 text-sm bg-orange-500 hover:bg-orange-600 text-white font-medium px-4 py-2 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaLink /> Register with Daraja
          </button>
          {(!confirmUrl || !validateUrl) && (
            <p className="text-xs text-amber-700 mt-2">Save a Callback Secret Token first to enable registration.</p>
          )}
        </div>
      </div>

      {/* Save */}
      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-medium px-6 py-2.5 rounded-xl transition-colors disabled:opacity-60"
      >
        <FaSave /> {saving ? 'Saving…' : 'Save Settings'}
      </button>

      <MpesaRegisterModal
        isOpen={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        confirmUrl={savedConfirmUrl}
        validateUrl={savedValidateUrl}
        environment={savedEnvironment}
      />
    </div>
  );
};

export default MpesaSettings;
